import { useEffect, useMemo, useState } from 'react'
import { Trash2, RotateCcw, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { recycleBinService, RecycleBinItem, RecycleBinEntityType } from '../services/recycleBin'
import { useLabels } from '../contexts/LabelsContext'
import type { ApiErrorLike } from '../utils/apiError'

const ENTITY_FILTERS: { key: 'all' | RecycleBinEntityType; labelKey?: 'project' | 'task' | 'portfolio' | 'program' | 'department' | 'team' }[] = [
  { key: 'all' },
  { key: 'project', labelKey: 'project' },
  { key: 'task', labelKey: 'task' },
  { key: 'portfolio', labelKey: 'portfolio' },
  { key: 'program', labelKey: 'program' },
  { key: 'department', labelKey: 'department' },
  { key: 'team', labelKey: 'team' },
]

export default function RecycleBin() {
  const { label } = useLabels()
  const [items, setItems] = useState<RecycleBinItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | RecycleBinEntityType>('all')
  const [restoring, setRestoring] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try {
      const data = await recycleBinService.list(filter === 'all' ? undefined : filter)
      setItems(data)
    } catch {
      toast.error('Failed to load recycle bin')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const i of items) c[i.entity_type] = (c[i.entity_type] || 0) + 1
    return c
  }, [items])

  async function restore(item: RecycleBinItem) {
    const key = `${item.entity_type}-${item.id}`
    setRestoring(key)
    try {
      await recycleBinService.restore(item.restore_path)
      toast.success(`Restored ${item.name}`)
      setItems(prev => prev.filter(x => !(x.entity_type === item.entity_type && x.id === item.id)))
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err?.response?.data?.detail ?? `Failed to restore ${item.name}`)
    } finally {
      setRestoring(null)
    }
  }

  function entityLabel(t: RecycleBinEntityType): string {
    return label(t as any)
  }

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Trash2 className="w-7 h-7 text-muted-foreground" />
          <h1 className="text-2xl font-semibold text-foreground">Recycle Bin</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Items deleted from anywhere in the app land here. Restore brings them back; permanent deletion happens after the org retention window.
        </p>

        <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Filter by entity type">
          {ENTITY_FILTERS.map(f => {
            const active = filter === f.key
            const lbl = f.key === 'all'
              ? 'All'
              : entityLabel(f.key as RecycleBinEntityType)
            const count = f.key === 'all' ? items.length : (counts[f.key] || 0)
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-input hover:bg-muted/30'
                }`}
              >
                {lbl}
                <span className="ml-1.5 opacity-70">({count})</span>
              </button>
            )
          })}
        </div>

        <div className="rounded-xl border bg-card shadow-sm">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Trash2 className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">Nothing here. Deleted items will appear in this list.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(item => {
                const key = `${item.entity_type}-${item.id}`
                const busy = restoring === key
                return (
                  <li key={key} className="flex items-start justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">
                          {entityLabel(item.entity_type)}
                        </span>
                        {item.deleted_at && (
                          <span className="text-xs text-muted-foreground">
                            • {new Date(item.deleted_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-foreground truncate">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => restore(item)}
                      disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-input bg-background hover:bg-muted/30 disabled:opacity-50"
                      aria-label={`Restore ${item.name}`}
                    >
                      {busy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                      Restore
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
